
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ProductTour from 'src/components/docs/ProductTour/ProductTour';
export const metadata = {
  toc: true,
  layout: 'reference',
  description: "Use Plaid Link to connect to your users' financial accounts with the Plaid API. Link handles credential validation, MFA, errors, and more.",
  preloadImages: [{
    src: '/assets/img/docs/docs-link-overview.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const TokenExchangeFlow = makeShortcode("TokenExchangeFlow");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link overview' subtitle="Use Link to connect to your users' financial accounts with the Plaid API" mdxType="Header" />
    <h4 {...{
      "id": "introduction-to-link"
    }}>{`Introduction to Link`}</h4>
    <p>{`Plaid Link is the client-side component that your users will interact with in order to link their accounts to Plaid and allow you to access their accounts via the Plaid API. Using Link is mandatory for all Plaid integrations, except for ones using only the handful of products that do not require end user interaction, such as `}<a parentName="p" {...{
        "href": "/docs/enrich"
      }}>{`Enrich`}</a>{` or the `}<a parentName="p" {...{
        "href": "/docs/identity-verification/#data-source-checks-without-ui-backend-flow"
      }}>{`Identity Verification backend-only flow`}</a>{`.`}</p>
    <ProductTour steps={[{
      text: 'An example Link flow. Your exact flow may differ.',
      image: '/assets/img/docs/link-tour/link-1-rm.png'
    }, {
      text: '...the user selects their bank...',
      image: '/assets/img/docs/link-tour/link-2-original-backup.png'
    }, {
      text: '...is directed to its OAuth flow...',
      image: '/assets/img/docs/link-tour/link-3-original-backup.png'
    }, {
      text: '...signs in via OAuth...',
      image: '/assets/img/docs/link-tour/link-4.png'
    }, {
      text: '...selects which accounts to share...',
      image: '/assets/img/docs/link-tour/link-select-accounts.png'
    }, {
      text: '...and links their account!',
      image: '/assets/img/docs/link-tour/link-5.png'
    }, {
      text: 'In the guest flow, they can optionally save their account for fast access next time by entering a phone number.',
      image: '/assets/img/docs/link-tour/link-6.png'
    }, {
      text: 'Once the phone number is verified, Link will close automatically.',
      image: '/assets/img/docs/link-tour/link-7.png'
    }]} defaultGalleryView={true} mdxType="ProductTour" />
    <p>{`Plaid Link handles all aspects of the login and authentication experience, including credential validation, multi-factor authentication, error handling, and sending account linking confirmation emails. For institutions that use OAuth, Link also manages the OAuth handoff flow, bringing the user to their institution to log in, and then returning them to the Plaid Link experience within your app.`}</p>
    <p>{`To try Link, see `}<a parentName="p" {...{
        "href": "https://plaid.com/link-demo/"
      }}>{`Plaid Link Demo`}</a>{`. `}</p>
    <p>{`Link is the only available method for connecting accounts and authenticating users in Production. In the Sandbox test environment, Link can optionally be bypassed for testing purposes via `}<inlineCode parentName="p">{`/sandbox/public_token/create`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "link-supported-platforms-and-presentation-modes"
    }}>{`Link supported platforms and presentation modes`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`For...`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Use...`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Best mobile UX`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Native `}<a parentName="td" {...{
              "href": "/docs/link/ios/"
            }}>{`iOS`}</a>{`, `}<a parentName="td" {...{
              "href": "/docs/link/android/"
            }}>{`Android`}</a>{`, `}<a parentName="td" {...{
              "href": "/docs/link/react-native/"
            }}>{`React Native`}</a>{`, or `}<a parentName="td" {...{
              "href": "/docs/link/flutter/"
            }}>{`Flutter`}</a>{` SDKs`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Best web UX`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/link/web/"
            }}>{`Native web SDK`}</a>{` (also available with React wrapper)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Webviews, iFrames, no frontend, or embedded flows where you don't control the frontend`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/link/hosted-link/"
            }}>{`Hosted Link`}</a></td>
        </tr>
      </tbody>
    </table>
    <p>{`There are also two optional Link presentation modes you can enable.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`For...`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Use...`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Driving the most end users to choose Plaid`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/link/embedded-institution-search/"
            }}>{`Embedded Institution Search`}</a></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Linking multiple banks in a single session (e.g. for PFM use cases)`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><a parentName="td" {...{
              "href": "/docs/link/multi-item-link/"
            }}>{`Multi-Item Link`}</a></td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "customizing-and-optimizing-link"
    }}>{`Customizing and optimizing Link`}</h4>
    <p>{`You can customize parts of Link's flow straight from the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/link"
      }}>{`Dashboard`}</a>{`. You can preview your changes in real time and then publish them instantly once you're ready to go live. For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/customization"
      }}>{`Link customization`}</a>{`.`}</p>
    <p>{`To help you take advantage of the options available for customizing and configuring Link, Plaid offers a `}<a parentName="p" {...{
        "href": "/docs/link/best-practices"
      }}>{`Link best practices guide`}</a>{` and an `}<a parentName="p" {...{
        "href": "/docs/link/messaging"
      }}>{`in-app messaging guide`}</a>{` for advice on how to present Link within your app.`}</p>
    <h4 {...{
      "id": "initializing-link"
    }}>{`Initializing Link`}</h4>
    <p>{`Link is initialized by passing the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` to Link. The exact implementation details for passing the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` will vary by platform. For detailed instructions, see the page for your specific platform: `}<a parentName="p" {...{
        "href": "/docs/link/web"
      }}>{`web`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/ios"
      }}>{`iOS`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/android"
      }}>{`Android`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/react-native"
      }}>{`React Native`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/flutter"
      }}>{`Flutter`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/webview/"
      }}>{`mobile webview`}</a>{`, or `}<a parentName="p" {...{
        "href": "/docs/link/hosted-link"
      }}>{`Hosted Link`}</a>{`.`}</p>
    <p>{`For recommendations on configuring the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` for your use case, see `}<a parentName="p" {...{
        "href": "/docs/link/initializing-products/"
      }}>{`Choosing how to initialize products`}</a>{`.`}</p>
    <h4 {...{
      "id": "link-flow-overview"
    }}>{`Link flow overview`}</h4>
    <p>{`Most Plaid products use Link to generate `}<inlineCode parentName="p">{`public_tokens`}</inlineCode>{`. The diagram below shows a model of how Link is used to obtain a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`, which can then be exchanged for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, which is used to authenticate requests to the Plaid API. `}</p>
    <p>{`Note that some products (including Plaid Check, Identity Verification, Monitor, Document Income, and Payroll Income) do not use a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` or `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`. For those products, you will call product endpoints once the end user has completed Link; see product-specific documentation for details on the flow. `}</p>
    <TokenExchangeFlow mdxType="TokenExchangeFlow" />
    <p>{`In code, this flow is initiated by creating a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` and using it to initialize Link. The
`}<inlineCode parentName="p">{`link_token`}</inlineCode>{` can be configured with the Plaid products you will be using and the countries you will
need to support. `}</p>
    <p>{`Once the user has logged in via Link, Link will issue a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`. You can obtain the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` through either the frontend or the backend:`}</p>
    <ul>
      <li parentName="ul">{`On the frontend: From the client-side `}<inlineCode parentName="li">{`onSuccess`}</inlineCode>{` callback returned by Link after a successful session. For more details on this method, see the Link frontend documentation for your specific platform. `}</li>
      <li parentName="ul">{`On the backend: From the `}<inlineCode parentName="li">{`/link/token/get`}</inlineCode>{` endpoint or opt-in `}<a parentName="li" {...{
          "href": "/docs/api/link/#session_finished"
        }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook after the Link session has been completed successfully. For more details on this method, see the `}<a parentName="li" {...{
          "href": "/docs/link/hosted-link"
        }}>{`Hosted Link`}</a>{` documentation.`}</li>
    </ul>
    <p>{`The `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` can then be exchanged for an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` via `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{`. `}</p>
    <h4 {...{
      "id": "error-handling-flows"
    }}>{`Error-handling flows`}</h4>
    <p>{`If your application will access an Item on a recurring basis, rather than just once, it should support `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`. Update mode allows you to refresh an Item if it enters an error state, such as when a user changes their password or MFA information. For more information, see `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`Updating an Item`}</a>{`.`}</p>
    <p>{`It's also recommended to have special handling for when a user attempts to link the same Item twice. Requesting access tokens for duplicate Items can lead to higher bills and end-user confusion. To learn more, see `}<a parentName="p" {...{
        "href": "/docs/link/duplicate-items"
      }}>{`preventing duplicate Items`}</a>{`.`}</p>
    <p>{`Occasionally, Link itself can enter an error state if the user takes too long to complete the Link process; a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` expires after 4 hours, or after 30 minutes if it was created for an existing Item in update mode. For information on handling this flow, see `}<a parentName="p" {...{
        "href": "/docs/link/handle-invalid-link-token"
      }}>{`Handling invalid Link tokens`}</a>{`.`}</p>
    <h4 {...{
      "id": "supporting-oauth"
    }}>{`Supporting OAuth`}</h4>
    <p>{`Many institutions use an OAuth authentication flow, in which Plaid Link redirects the end user to their bank's website or mobile app to authenticate. To learn more, see the `}<a parentName="p" {...{
        "href": "/docs/link/oauth"
      }}>{`OAuth guide`}</a>{`.`}</p>
    <h4 {...{
      "id": "returning-user-flows"
    }}>{`Returning user flows`}</h4>
    <p>{`The returning user flow allows you to enable a faster Plaid Link experience for your users who already use Plaid. To learn more, see `}<a parentName="p" {...{
        "href": "/docs/link/returning-user"
      }}>{`Returning user experience`}</a>{`.`}</p>
    <h4 {...{
      "id": "troubleshooting"
    }}>{`Troubleshooting`}</h4>
    <p>{`Since all your users will go through Link, it's important to build as robust an integration as possible. For details on dealing with common problems, see the `}<a parentName="p" {...{
        "href": "/docs/link/troubleshooting"
      }}>{`Troubleshooting`}</a>{` section.`}</p>
    <h4 {...{
      "id": "link-updates"
    }}>{`Link updates`}</h4>
    <p>{`Plaid periodically updates Link to add new functionality and improve conversion. These changes will be automatically deployed. Any test suites and business logic in your app should be robust to the possibility of changes to the user-facing Link flow.`}</p>
    <p>{`Users of Plaid's SDKs for React, React Native, Flutter, iOS, and Android should regularly update to ensure support for the latest client platforms and Plaid functionality.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "introduction-to-link",
        level: 4,
        title: "Introduction to Link",
      },
{
        id: "link-supported-platforms-and-presentation-modes",
        level: 4,
        title: "Link supported platforms and presentation modes",
      },
{
        id: "customizing-and-optimizing-link",
        level: 4,
        title: "Customizing and optimizing Link",
      },
{
        id: "initializing-link",
        level: 4,
        title: "Initializing Link",
      },
{
        id: "link-flow-overview",
        level: 4,
        title: "Link flow overview",
      },
{
        id: "error-handling-flows",
        level: 4,
        title: "Error-handling flows",
      },
{
        id: "supporting-oauth",
        level: 4,
        title: "Supporting OAuth",
      },
{
        id: "returning-user-flows",
        level: 4,
        title: "Returning user flows",
      },
{
        id: "troubleshooting",
        level: 4,
        title: "Troubleshooting",
      },
{
        id: "link-updates",
        level: 4,
        title: "Link updates",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
